import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Package } from 'lucide-react';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { SplitStatusChip } from '@/shared/ui/chips/SplitStatusChip';
import { extractErrorMessage } from '@/shared/api/errors';
import { useArrivedParcelsList } from '@/entities/warehouse-arrivals';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import type { ApiArrivedParcelItem } from '@/shared/types/api/warehouse-arrivals';

const PAGE_SIZE = 20;

export const WarehouseArrivedParcelsTab = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = useArrivedParcelsList(params);

  const columns = useMemo<ColumnDef<ApiArrivedParcelItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('warehouseArrivals.parcels.columns.name'),
        cell: ({ row }) => <span className="block truncate">{row.original.name || '—'}</span>,
      },
      {
        accessorKey: 'trek_id',
        header: t('warehouseArrivals.parcels.columns.trekId'),
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.trek_id}</span>,
      },
      {
        id: 'pinfl',
        enableSorting: false,
        header: t('warehouseArrivals.parcels.columns.pinfl'),
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.pinfl || '—'}</span>,
      },
      {
        id: 'delivered',
        enableSorting: false,
        header: t('warehouseArrivals.parcels.columns.delivered'),
        cell: ({ row }) => {
          const d = row.original.delivered_to_customer;
          return (
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                d ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {d ? t('warehouseArrivals.parcels.delivered.yes') : t('warehouseArrivals.parcels.delivered.no')}
            </span>
          );
        },
      },
      {
        id: 'split',
        enableSorting: false,
        header: t('warehouseArrivals.parcels.columns.split'),
        cell: ({ row }) =>
          row.original.is_split && row.original.split_status ? (
            <SplitStatusChip status={row.original.split_status} />
          ) : (
            <span className="text-ink-muted">—</span>
          ),
      },
      {
        id: 'actions',
        enableSorting: false,
        header: t('warehouseArrivals.parcels.columns.actions'),
        cell: ({ row }) => (
          <IconButton
            label={t('warehouseArrivals.parcels.view')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/warehouse/arrived-parcels/${row.original.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </IconButton>
        ),
      },
    ],
    [navigate, t],
  );

  return (
    <div className="space-y-4">
      <FilterBar
        searchValue={search}
        onSearchChange={(v) => {
          setPage(1);
          setSearch(v);
        }}
        searchPlaceholder={t('warehouseArrivals.parcels.searchPlaceholder')}
      />

      {query.error ? (
        <ErrorState
          message={extractErrorMessage(query.error, t('warehouseArrivals.parcels.loadError'))}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          <DataTable
            data={query.data?.results ?? []}
            columns={columns}
            isLoading={query.isLoading}
            emptyTitle={t('warehouseArrivals.parcels.emptyTitle')}
            emptyDescription={t('warehouseArrivals.parcels.emptyDescription')}
            emptyIcon={<Package className="h-8 w-8" aria-hidden />}
            onRowClick={(row) => navigate(`/warehouse/arrived-parcels/${row.id}`)}
            getRowId={(row) => row.id}
            numbered
            pageIndex={page - 1}
            pageSize={PAGE_SIZE}
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={query.data?.count ?? 0}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};
